<template lang='pug'>
  .container.inner-header
    .points-content
      .content.card-info.info-container
        img.icon-card(:src='cardIconUrl' :alt='`${tierCard}-card`')
        .name(v-text='userName')
      .break-line(v-if="isMobile")
      .content.point-balance
        .points(v-text='points')
        .points-type(v-text='$t("pointExpiry.pointBalance")')
        .due-date(v-text='$t("pointExpiry.asOfToday")')
      .content.point-expiring
        .points(v-text='pointsExpiring')
        .points-type(v-text='$t("pointExpiry.pointExpiring")')
        .due-date(v-text='pointExpiringDueDateText')

    .tier-progress
      .progress
        template(v-if='tier === "gold"')
          .gold-notice(v-text='$t("goldNotice")')
        template(v-else)
          .bar
            .fill(:style='{ width: progressPercentage + "%" }')
          .svg-icon.card-icon(:class='`${nextTierIconClass}`')
      .text(v-text='tagline')
</template>

<i18n>
{
  "lng_en": {
    "firstPurchase": "Make your first purchase and start enjoying your Beauty Pass benefits!",
    "tierTagline": "Spend %{amount} by %{expiry} to become a %{nextTier} member!",
    "goldTagline": "Spend %{amount} by %{expiry} to maintain your status",
    "goldNotice": "Congratulations! You're a Gold member!"
  },
  "lng_id": {
    "firstPurchase": "Lakukan pembelanjaan pertama dan segera nikmati keuntungan Beauty Pass Anda!",
    "tierTagline": "Belanja %{amount} sebelum %{expiry]} untuk menjadi %{nextTier} member",
    "goldTagline": "Belanja %{amount} sebelum %{expiry} untuk mempertahankan status Anda.",
    "goldNotice": "Selamat! Anda adalah Gold member!"
  },
  "lng_th": {
    "firstPurchase": "เริ่มชอปครั้งแรก และรับสิทธิพิเศษมากมายจาก Beauty Pass ",
    "tierTagline": "ชอป %{amount} ภายใน %{expiry} เพื่อเป็นสมาชิก Sephora %{nextTier}!",
    "goldTagline": "ชอป %{amount} ภายใน %{expiry} เพื่อรักษาสถานะของคุณ",
    "goldNotice": "ยินดีด้วย! คุณเป็นสมาชิก Sephora Gold แล้ว! "
  },
  "lng_zh": {
    "firstPurchase": "立即開始購物，享受Beauty Pass好處！",
    "tierTagline": "%{expiry}前購物滿%{amount}即可成為%{nextTier}卡會員！",
    "goldTagline": "%{expiry}前購物滿%{amount}即可延續會籍。",
    "goldNotice": "恭喜！你已成為Gold卡會員！"
  },
  "lng_ko": {
    "firstPurchase": "첫 구매 후 뷰티 패스 멤버십의 혜택을 누리세요! ",
    "tierTagline": "%{expiry}까지 %{amount} 더 구매하고 %{nextTier} 멤버가 되세요! ",
    "goldTagline": "%{expiry}까지 %{amount} 더 구매하고 현재 등급을 유지하세요",
    "goldNotice": "축하합니다! 골드 멤버가 되셨습니다! "
  }
}
</i18n>

<script>
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import { humanizePrice } from '@/modules/services/price-support'

export default {
  props: {
    user: Object
  },

  data () {
    return {
      tier: this.user['loyalty-tier'],
      progress: this.user['progress-to-next-tier']
    }
  },

  computed: {
    isMobile: () => vueSupport.isMobile,
    userName () {
      return this.user['first-name']
    },
    points () {
      return this.user['points-formatted']
    },
    pointsExpiring () {
      return `${this.user['soonest-expiring-points']}`
    },
    tagline () {
      switch (this.tier) {
        case 'pink':
          return `${this.$t('firstPurchase')}`
        case 'gold':
          return this.generateGoldTagline()
        default:
          return `${this.$t('tierTagline', {
            amount: this.humanizeAmount(this.amountToNextTier),
            expiry: this.tierExpiry,
            nextTier: this.nextTierText
          })}`
      }
    },
    tierCard () {
      return this.tier === 'pink' ? 'white' : this.tier
    },
    cardIconUrl () {
      return require(`@/assets/images/icons/beauty-pass-${this.tierCard}-tier.png`)
    },
    tierExpiry () {
      return format(parse(this.user['tier-expiry-date']), 'D MMMM YYYY')
    },
    nextTier () {
      switch (this.tier) {
        case 'pink':
        case 'white':
          return 'black'
        case 'black':
          return 'gold'
        default:
          return ''
      }
    },
    nextTierIconClass () {
      return `${this.nextTier}-card-new`
    },
    nextTierText () {
      const nextTier = this.nextTier
      return nextTier.charAt(0).toUpperCase() + nextTier.substr(1)
    },
    amountToNextTier () {
      return this.user['amount-to-next-tier'] / 100
    },
    amountToRetain () {
      return this.user['amount-to-retain'] / 100
    },
    progressPercentage () {
      return Math.round(this.progress * 100)
    },
    pointExpiringDueDateText () {
      return this.$t('pointExpiry.onDate', { date: this.user['soonest-expiry-date'] })
    }
  },

  methods: {
    humanizeAmount (amount) {
      return humanizePrice(amount * 100, { showOriginalCurrency: true })
    },
    generateGoldTagline () {
      if (this.amountToRetain <= 0) {
        return ''
      }

      return `${this.$t('goldTagline', {
        amount: this.humanizeAmount(this.amountToRetain),
        expiry: this.tierExpiry
      })}`
    }
  }
}
</script>

<style lang='scss' scoped>
  .inner-header {
    align-items: center;
    align-self: flex-end;
    bottom: -74px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    padding-left: 20px;
    padding-right: 20px;
    position: relative;
    width: 310px;
    margin-top: 16px;

    @include media-breakpoint-up(sm) {
      bottom: -70px;
      width: 480px;
    }

    @include media-breakpoint-up(xl) {
      margin: 0 auto;
      padding-top: 20px;
      position: static;
      width: 630px;
      flex-flow: column;
    }

    > .svg-icon {
      height: 70px;
      margin-right: 8px;
      width: 110px;

      @include media-breakpoint-up(xl) {
        flex: 0 0 150px;
        margin-right: 20px;
        width: 150px;
      }
    }

    > .points-content {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-around;
      align-items: center;
      width: 400px;
      height: 113px;

      @include media-breakpoint-up(xl) {
        flex-flow: row;
        width: 400px;
      }

      @media (max-width: 912px) {
        height: 100px;
      }

      @include media-breakpoint-down(sm) {
        height: auto;
        justify-content: center;
      }

      > .content {
        text-align: center;
        text-transform: capitalize;
        letter-spacing: 1px;
        font-family: 'Sephora Sans';
        font-style: normal;
        font-size: 14px;
        line-height: 21px;
        display: flex;
        flex-flow: column;
        align-items: center;
        justify-content: center;
        height: inherit;
        margin-bottom: 10px;

        @include media-breakpoint-down(sm) {
          margin-bottom: 16px;
        }
      }

      .break-line {
        @include media-breakpoint-down(sm) {
          flex-basis: 100%;
          height: 0
        }
      }

      > .point-balance, .point-expiring {
        > .points {
          @include font-weight-bold;
          font-weight: 400;
          font-size: 28px;
          line-height: 35px;
        }

        > .points-type {
          @include font-weight-bold;

          padding: 6px 0;
        }
      }

      > .point-balance {
        border-style: none solid;
        border-color: $grey-light-e5;

        @include media-breakpoint-up(sm) {
          padding: 0 16px;
          border-style: none solid !important;
        }

        @include media-breakpoint-down(sm) {
          border-style: none solid none none;
          padding-right: 11px;
        }
      }

      > .point-expiring {
        @include media-breakpoint-down(sm) {
          padding-left: 11px;
        }
      }
    }

    > .tier-progress {
      width: 100%;
      max-width: 400px;
      margin-top: 8px;

      > .progress {
        align-items: center;
        display: flex;
        flex: 1 100%;

        @include media-breakpoint-up(xl) {
          flex: 0 auto;
        }
      }

      > .text {
        @include media-breakpoint-up(xl) {
          text-align: left;
        }
      }
    }
  }

  .progress {
    > .gold-notice {
      margin-left: auto;
      margin-right: auto;
      margin-top: 10px;

      @include media-breakpoint-up(xl) {
        margin-left: 0;
        margin-right: 0;
      }
    }

    > .bar {
      background-color: #D8D8D8;
      flex-grow: 1;
      height: 8px;
    }

    > .svg-icon {
      height: 40px;
      margin-left: -10px;
      margin-right: -10px;
      width: 60px;
    }
  }

  .header-wrapper {
    @include media-breakpoint-up(sm) {
      background-size: cover;
      flex-direction: column;
      height: 170px;
      justify-content: center;
      margin-bottom: 0;
      width: 80%;
      padding: 10px;
    }

    .inner-header {

      .card-info {
        @include media-breakpoint-between(xs, sm) {
          padding-right: 20px;
          margin-top: 5px;
          width: auto !important;
        }

        @include media-breakpoint-down(lg) {
          margin-top: 5px;
        }

        @include media-breakpoint-down(sm) {
          height: 100px;
          width: inherit;
        }

        .name {
          @include font-weight-medium;
        }
      }

      img.icon-card {
        width: 85px;
        height: 88px;
        margin-bottom: 5px;

        @include media-breakpoint-down(lg) {
          width: 80px; // for land tablet
          height: 85px;
        }

        @media (max-width: 768px) {
          width: 75px; // for portrait tablet
          height: 80px;
        }
      }
      @include media-breakpoint-up(sm) {
        bottom: 0;
      }

      @include media-breakpoint-down(sm) {
        bottom: 0;
      }
    }
  }

  .bar {
    > .fill {
      background-color: $brand-primary;
      height: 8px;
    }
  }

  .white-card-new {
    background-image: url('../../../assets/images/icons/beauty-pass-white-tier.png');
  }

  .black-card-new {
    background-image: url('../../../assets/images/icons/beauty-pass-black-tier.png');
  }

  .gold-card-new {
    background-image: url('../../../assets/images/icons/beauty-pass-gold-tier.png');
  }

</style>
